import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

// Types
import type { Agent, Office } from "types";

// UI (your form component)
import AgentForm from "./AgentsAddEditComponent";

// Toast
import { showSuccess, showError } from "toast/toast";
import { uploadCloudFile, getCloudFileById, resolveImageId, hardDeleteCloudFile } from "components/Imageshelper";
import { getAuthCookie } from "../utils/authCookies";
// Field validations (same ones you use today)
import {
  firstNameValidation,
  lastNameValidation,
  emailValidation,
  phoneValidation,
  phone2Validation,
  passwordValidation,
  statusValidation,
  addressLine1Validation,
  cityValidation,
  stateValidation,
  zipcodeValidation,
} from "validationSchemas/common";

// ---------- Helpers ----------
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

function getOfficeById(offices: Office[], officeId: string) {
  return offices?.find((o) => (o as any)._id === officeId) || null;
}
const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};

// ---------- Single Upsert Container ----------
const RealtorAddEditAgent: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = !!agentId;

  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";
  const userName = getAuthCookie("userName") || "";
const userId = getAuthCookie("userId");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const [brokerageFirmDetails, setBrokerageFirmDetails] = useState<any>(null);
  
  // State for parent brokerage firm (for teammember login)
  const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
  const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);

  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };

  // Fetch parent hierarchy for teammember login
  const fetchParentHierarchy = async () => {
    if (!isTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "BROKERAGE_FIRM"
        const brokerageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "BROKERAGE_FIRM"
        );
        
        if (brokerageFirm) {
          setParentBrokerageId(brokerageFirm.id || brokerageFirm._id);
          setParentBrokerageName(brokerageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };

  // Fetch parent hierarchy for teammember login
  useEffect(() => {
    if (isTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isTeammember, token]);

  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch {
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // Fetch brokerage firm details to get company name
  // For teammember: fetch parent brokerage firm details
  // For realtor: fetch own brokerage firm details
  useEffect(() => {
    (async () => {
      // Wait for parent hierarchy to load for teammember
      if (isTeammember && !parentBrokerageId) return;
      
      const brokerageIdToFetch = isTeammember ? parentBrokerageId : userId;
      if (!brokerageIdToFetch) return;

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${brokerageIdToFetch}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();
        if (res.ok) {
          setBrokerageFirmDetails(data);
          // Set the default brokerage firm affiliation
          const companyName = data?.firmDetails?.companyName || (isTeammember ? parentBrokerageName : userName);
          if (companyName && !formik.values.brokerageFirmAffiliation) {
            formik.setFieldValue("brokerageFirmAffiliation", companyName);
          }
        }
      } catch (err: any) {
        console.error("Failed to load brokerage firm details:", err);
      }
    })();
  }, [isTeammember, parentBrokerageId, userId, token]);

  // Load offices (both add + edit)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/offices`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();
        setOffices(Array.isArray(data) ? data : []);
      } catch (err: any) {
        showError(err?.message || "Failed to load offices");
      }
    })();
  }, [token]);

  // Load agent (edit only)
  useEffect(() => {
    (async () => {
      if (!isEdit || !agentId) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${agentId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (data) setAgent(data as Agent);
      } catch (error: any) {
        showError(error?.message || "Failed to fetch agent data");
      }
    })();
  }, [isEdit, agentId, token]);

  // Options for selects
  const officeOptions = useMemo(
    () => (offices || []).map((o) => ({ value: (o as any)._id, label: (o as any).officeName })),
    [offices]
  );

  // Brokerage firm options - show parent company for teammember, own company for realtor
  const brokerageFirmOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    
    // For teammember: use parent brokerage firm
    // For realtor: use own brokerage firm
    const companyName = brokerageFirmDetails?.firmDetails?.companyName || (isTeammember ? parentBrokerageName : userName) || "";
    
    if (companyName) {
      options.push({ value: companyName, label: companyName });
    }
    
    return options;
  }, [brokerageFirmDetails, isTeammember, parentBrokerageName, userName]);

  // Validation schema (email/password required only on Add)
  const validationSchema = useMemo(() => {
    const base = {
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      phone2: phone2Validation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    };

    if (isEdit) {
      return Yup.object(base);
    } else {
      return Yup.object({
        ...base,
        email: emailValidation,
        // password: passwordValidation,
      });
    }
  }, [isEdit]);

  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Personal
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      email: agent?.email || "",
      password: "",

      // Professional
      officeId: (agent as any)?.offices?.[0]?._id || "",
      agentID: (agent as any)?.agentDetails?.agentID || "",
      licenseNumber: (agent as any)?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: (agent as any)?.agentDetails?.licenseExpiryDate || "",
      brokerageFirmAffiliation: brokerageFirmDetails?.firmDetails?.companyName || (agent as any)?.agentDetails?.brokerageFirmAffiliation || "",

      // Address
      addressLine1: (agent as any)?.firmDetails?.address?.addressLine1 || "",
      addressLine2: (agent as any)?.firmDetails?.address?.addressLine2 || "",
      city: (agent as any)?.firmDetails?.address?.city || "",
      state: (agent as any)?.firmDetails?.address?.state || "",
      zipcode: (agent as any)?.firmDetails?.address?.zip || "",
      cityCode: (agent as any)?.firmDetails?.address?.cityCode || "",
      stateCode: (agent as any)?.firmDetails?.address?.stateCode || "",
      country: (agent as any)?.firmDetails?.address?.country || "",
      countryCode: (agent as any)?.firmDetails?.address?.countryCode || "",
      latitude: (agent as any)?.firmDetails?.address?.latitude || 0,
      longitude: (agent as any)?.firmDetails?.address?.longitude || 0,

      // Misc
      statuss: (agent as any)?.status || "Active" || "",
      image: null as File | null,
      displaySrc: agent?.profilePic || null,
      profilePic: agent?.profilePic || null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        let imageFileId = cloudFileId || ""; // keep existing if present
        
        // ✅ Delete old image if a new one is being uploaded
        if (values.image instanceof File && cloudFileId && /^[0-9a-f]{24}$/i.test(cloudFileId)) {
          try {
            await hardDeleteCloudFile(cloudFileId, token);
            // console.log(`Old profile image ${cloudFileId} deleted successfully`);
          } catch (deleteError) {
            console.warn(`Failed to delete old profile image ${cloudFileId}:`, deleteError);
            // Continue even if delete fails - don't block the update
          }
        }
        
        // Upload new image
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }
        
        const generated_id = await resolveImageId(values.image, token, imageFileId);

        // Office mapping
        const selectedOffice = getOfficeById(offices, values.officeId);

        if (isEdit && agentId) {
          // UPDATE
          const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${agentId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
              userType: "agent",
              firstName: values.firstName,
              lastName: values.lastName,
              // phone2: withDefault(values.phone2),
              phone1: withDefault(values.phone1),
              // phone1: values.phone1,
              phone2: values.phone2,
              licenseType: "BROKERAGE_AGENT",
              status: values.statuss,
              profilePic: generated_id || "",
              firmDetails: {
                companyID: "",
                companyName: "",
                companyWebsite: "",
                address: {
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  state: values.state,
                  country: values.country,
                  zip: values.zipcode,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: values.countryCode,
                  latitude: values.latitude,
                  longitude: values.longitude,
                  phone: withDefault(values.phone1),
                },
              },
              agentDetails: {
                brokerageFirmAffiliation:
                  values.brokerageFirmAffiliation || brokerageFirmDetails?.firmDetails?.companyName || (agent as any)?.agentDetails?.brokerageFirmAffiliation || "",
                brokerageFirmName:
                  values.brokerageFirmAffiliation || brokerageFirmDetails?.firmDetails?.companyName || (agent as any)?.agentDetails?.brokerageFirmName || (agent as any)?.agentDetails?.brokerageFirmAffiliation || "",
                brokerageFirmID:
                  (isTeammember ? parentBrokerageId : userId) || (agent as any)?.agentDetails?.brokerageFirmID || "",
                agentID: values.agentID,
                licenseNumber: values.licenseNumber,
                licenseExpiryDate: values.licenseExpiryDate,
                licenseActive: (agent as any)?.agentDetails?.licenseActive ?? false,
              },
              offices: selectedOffice
                ? [
                    {
                      _id: (selectedOffice as any)?._id,
                      officeName: (selectedOffice as any)?.officeName,
                      officeType: (selectedOffice as any)?.officeType,
                      officeAddress: {
                        _id: (selectedOffice as any)?.officeAddress?.[0]?._id || "",
                        addressLine1: (selectedOffice as any)?.officeAddress?.[0]?.addressLine1 || "",
                        city: (selectedOffice as any)?.officeAddress?.[0]?.city || "",
                        state: (selectedOffice as any)?.officeAddress?.[0]?.state || "",
                        country: (selectedOffice as any)?.officeAddress?.[0]?.country || "",
                        zip: (selectedOffice as any)?.officeAddress?.[0]?.zip || "",
                      },
                    },
                  ]
                : [],
            }),
          });
          if (!response.ok) throw new Error("Failed to save data");
          showSuccess("Agent updated successfully!");
          navigate(`${baseRoute}/agents`);
        } else {
          // CREATE
          const response = await fetch(`${process.env.REACT_APP_API_URL}/user/create`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
              userType: "agent",
              firstName: values.firstName,
              lastName: values.lastName,
              // phone2: withDefault(values.phone2),
              phone1: withDefault(values.phone1),
              // phone1: values.phone1,
              phone2: values.phone2,
              // phone1: values.phone1,
              // phone2: values.phone2,
              licenseType: "BROKERAGE_AGENT",
              status: values.statuss,
              profilePic: generated_id || "",
              firmDetails: {
                companyID: "",
                companyName: "",
                companyWebsite: "",
                address: {
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  state: values.state,
                  country: values.country,
                  zip: values.zipcode,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: values.countryCode,
                  latitude: values.latitude,
                  longitude: values.longitude,
                  phone: withDefault(values.phone1),
                },
              },
              agentDetails: {
                brokerageFirmAffiliation: values.brokerageFirmAffiliation || brokerageFirmDetails?.firmDetails?.companyName || (isTeammember ? parentBrokerageName : userName) || "",
                brokerageFirmName: values.brokerageFirmAffiliation || brokerageFirmDetails?.firmDetails?.companyName || (isTeammember ? parentBrokerageName : userName) || "",
                brokerageFirmID: (isTeammember ? parentBrokerageId : userId) || "",
                agentID: values.agentID,
                licenseNumber: values.licenseNumber,
                licenseExpiryDate: values.licenseExpiryDate,
                licenseActive: false,
              },
              offices: selectedOffice
                ? [
                    {
                      _id: (selectedOffice as any)?._id,
                      officeName: (selectedOffice as any)?.officeName,
                      officeType: (selectedOffice as any)?.officeType,
                      officeAddress: {
                        _id: (selectedOffice as any)?.officeAddress?.[0]?._id || "",
                        addressLine1: (selectedOffice as any)?.officeAddress?.[0]?.addressLine1 || "",
                        city: (selectedOffice as any)?.officeAddress?.[0]?.city || "",
                        state: (selectedOffice as any)?.officeAddress?.[0]?.state || "",
                        country: (selectedOffice as any)?.officeAddress?.[0]?.country || "",
                        zip: (selectedOffice as any)?.officeAddress?.[0]?.zip || "",
                      },
                    },
                  ]
                : [],
              created_by:{id:userId,name:userName},
            }),
          });

          const result = await response.json();
          if (!response.ok) {
            showError(result?.error || "Something went wrong");
            return;
          }

          const hasAddressDetails = [
            values.addressLine1,
            values.city,
            values.state,
            values.zipcode,
          ].some((field) => field?.trim());

          // Best-effort: create address record
          if (hasAddressDetails) {
            try {
              const addressResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/addresses/user/${result.id}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    user_id: `${result.id}`,
                    label: `${values.firstName} ${values.lastName}`,
                    first_name: values.firstName,
                    last_name: values.lastName,
                    company: "",
                    address_details: {
                      label: `${values.firstName} ${values.lastName}`,
                      addressLine1: values.addressLine1,
                      addressLine2: values.addressLine2,
                      city: values.city,
                      state: values.state,
                      zip: values.zipcode,
                      country: values.country,
                      // phone2: withDefault(values.phone2),
                      phone: withDefault(values.phone1),
                      // phone1: values.phone1,
                      // phone2: values.phone2,
                      // phone: values.phone1,
                      cityCode: values.cityCode,
                      stateCode: values.stateCode,
                      countryCode: values.countryCode,
                      latitude: values.latitude,
                      longitude: values.longitude,
                    },
                  }),
                }
              );
              if (!addressResponse.ok) {
                const err = await addressResponse.json();
                throw new Error(err?.message || "Failed to add address");
              }
            } catch (addressErr: any) {
              // Non-blocking
              showError(`Address Error: ${addressErr.message}`);
            }
          }

          navigate(`${baseRoute}/agents`, { state: { successMessage: "Agent added successfully!" } });
        }
      } catch (err: any) {
        showError(isEdit ? "Failed to update Agent." : "Failed to add Agent.");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // Derived options for AgentForm
  const STATUS_OPTIONS = React.useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    []
  );

  // Handlers passed straight through
  const handleCancel = () => navigate(-1);
  const handleSubmit = () => formik.handleSubmit();
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);

  return (
    <AgentForm
      isEdit={isEdit}
      formik={formik}
      brokerageFirmOptions={brokerageFirmOptions}
      officeOptions={officeOptions}
      statusOptions={STATUS_OPTIONS}
      displaySrc={displaySrc}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitting={formSubmitting}
      type="Brokerage_Agent"
    />
  );
};

export default RealtorAddEditAgent;
